import { Box, Stack, Typography } from '@mui/material';
import { toast, useIsMobile } from '@side/pantry';
import { format } from 'date-fns';
import { useCallback, useState } from 'react';
import { DataGrid } from '@/components/DataGrid';
import { ExportToCSVButton } from '../../components/ExportToCSVButton';
import { mapDataGridColumnToValueOrEmpty } from '../../helpers';
import {
  buildCsv,
  downloadCsv,
} from '../../ReportsExport/ReportsExport.helper';
import { TablePagination } from '../../TablePagination/TablePagination';
import { useLeadSourcePerformance } from '../hooks/useLeadSourcePerformance';
import {
  DEFAULT_PAGE_SIZE,
  LeadSourcePerformanceColumns,
  PAGE_SIZE_OPTIONS,
} from './LeadSourcePerformanceTable.helpers';

interface Props {
  viewReportAs: string;
}

const mappedColumns = LeadSourcePerformanceColumns.map((column) =>
  mapDataGridColumnToValueOrEmpty(column),
);

export function LeadSourcePerformanceTable({ viewReportAs }: Props) {
  const isMobile = useIsMobile('md');
  const { rows, isLoading } = useLeadSourcePerformance(viewReportAs);

  const [isExporting, setIsExporting] = useState(false);

  const handleExportCsv = useCallback(async () => {
    if (rows.length === 0) return;

    setIsExporting(true);
    try {
      const csv = buildCsv(rows, LeadSourcePerformanceColumns);
      const filename = `Lead-Source-Performance-${format(
        new Date(),
        'yyyy-MM-dd',
      )}.csv`;

      downloadCsv(csv, filename);
      toast('success', 'Lead Source Performance exported successfully', {
        showCloseButton: true,
      });
    } catch {
      toast(
        'error',
        'Export failed. Please try again or contact Side Support for assistance.',
        { showCloseButton: true },
      );
    } finally {
      setIsExporting(false);
    }
  }, [rows]);

  const showPagination = rows.length > DEFAULT_PAGE_SIZE;

  return (
    <Stack sx={{ gap: 2 }} id="lead-source-performance-table">
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'center', md: 'flex-end' },
        }}
      >
        <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'}>
          Lead Source Performance
        </Typography>
        <ExportToCSVButton
          onClick={handleExportCsv}
          loading={isExporting}
          disabled={rows.length === 0 || isLoading}
        />
      </Stack>
      <Box sx={{ position: 'relative' }}>
        <DataGrid
          variant="default"
          columns={mappedColumns}
          rows={rows}
          loading={isLoading}
          disableColumnFilter
          initialState={{
            pagination: {
              paginationModel: {
                page: 0,
                pageSize: DEFAULT_PAGE_SIZE,
              },
            },
            sorting: {
              sortModel: [{ field: 'salesVolume', sort: 'desc' }],
            },
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          paginationMode="client"
          sortingMode="client"
          disableColumnMenu
          slots={{
            pagination: () => (
              <TablePagination
                variant="performance"
                hidePaginationControls={!showPagination}
              />
            ),
            noRowsOverlay: () => (
              <Typography
                variant="body3"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'text.secondary',
                }}
              >
                No performance data for selected filters
              </Typography>
            ),
          }}
          sx={{
            ...(rows.length === 0 && {
              '--DataGrid-overlayHeight': '60px',
            }),
            '& .MuiTablePagination-root': {
              justifyContent: 'flex-start',
              display: 'flex',
            },
            '& .MuiToolbar-root': {
              paddingLeft: 0,
            },
          }}
        />
      </Box>
    </Stack>
  );
}
